// <pfs-theme-toggle> — brand-agnostischer Light/Dark/System-Umschalter.
//
// PFS-486. Generisch + brand-agnostisch: Styling nur ueber --pfs-color-*-
// Tokens + neutrale Fallbacks (kein App-Hardcode). Konsumenten (mednet-main,
// sparkden-main, …) haengen das WC z.B. in ihren sidebar-header-Slot.
//
// Verhalten:
//   - Drei Modi, zyklisch per Klick: System → Hell → Dunkel → System.
//   - Persistiert den Modus in localStorage (data-storage-key).
//   - Wendet das aufgeloeste Theme als `data-theme="light|dark"` auf
//     <html> an (Document-Scope → Token-Systeme mit Custom-Properties
//     erben durch Shadow-Grenzen). Im System-Modus folgt es live
//     prefers-color-scheme.
//   - Dispatcht `pfs:theme-change` (bubbles + composed, detail {mode,
//     resolved}). Shadow-DOM-Konsumenten mit statischen [data-theme]-
//     Utility-Klassen IM Shadow (z.B. Tailwind) spiegeln `resolved` damit
//     auf ihren eigenen Shadow-Root — Custom-Properties allein reichen fuer
//     statische Klassen nicht ueber die Shadow-Grenze. Token-basierte
//     Konsumenten (Custom-Properties) erben das <html>-Attribut automatisch.
//
// Voraussetzung fuer manuelle Wahl in Token-Systemen (PFS-486 Teil 2):
//   Dark-Tokens in @pfs/design (+ @mednet/design) muessen zusaetzlich unter
//   `[data-theme="dark"]` reagieren (nicht nur @media prefers-color-scheme).
//
// Attribute (data-*):
//   data-storage-key  localStorage-Key (default "pfs-theme").
//   data-default      Initial-Modus ohne gespeicherte Wahl: "system" |
//                     "light" | "dark" (default "system").

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { ICON_BUTTON_CSS } from "../../styles/icon-button.js";

export type ThemeMode = "system" | "light" | "dark";
export const PFS_THEME_CHANGE_EVENT = "pfs:theme-change";

const MODES: ThemeMode[] = ["system", "light", "dark"];
const LABELS: Record<ThemeMode, string> = { system: "System", light: "Hell", dark: "Dunkel" };

// Inline-SVG (currentColor, 24er-Grid). System = Monitor, Hell = Sonne,
// Dunkel = Mond.
const ICONS: Record<ThemeMode, string> = {
  system: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>`,
  light: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>`,
  dark: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>`,
};

// Optik = gemeinsamer Icon-Button-Contract (host-stempelbar via --pfs-icon-btn-*).
// Kein Eigen-Chrome mehr (frueher: 1px-Border + radius .625 + eigener Hover) —
// neutraler, randloser Default, der jede Environment per Custom-Property stempelt.
const STYLES = `
${SHADOW_RESET_CSS}
:host { display: inline-block; }
${ICON_BUTTON_CSS}
`;

const resolve = (mode: ThemeMode): "light" | "dark" => {
  if (mode === "light" || mode === "dark") return mode;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

defineComponent({
  tagName: "pfs-theme-toggle",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;

    const storageKey = host.getAttribute("data-storage-key") || "pfs-theme";
    const fallback = (host.getAttribute("data-default") as ThemeMode) || "system";

    const readMode = (): ThemeMode => {
      const v = localStorage.getItem(storageKey);
      return v === "light" || v === "dark" || v === "system" ? v : fallback;
    };

    let mode: ThemeMode = readMode();

    const style = document.createElement("style");
    style.textContent = STYLES;
    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("part", "button"); // erlaubt Host-Styling via ::part(button) (z.B. Icon-Button-Optik)
    root.replaceChildren(style, button);

    const paint = (): void => {
      button.innerHTML = ICONS[mode];
      const label = `Theme: ${LABELS[mode]} (klicken zum Wechseln)`;
      button.setAttribute("aria-label", label);
      button.setAttribute("title", label);
    };

    const apply = (): void => {
      const resolved = resolve(mode);
      document.documentElement.setAttribute("data-theme", resolved);
      host.dispatchEvent(
        new CustomEvent(PFS_THEME_CHANGE_EVENT, {
          bubbles: true,
          composed: true,
          detail: { mode, resolved },
        }),
      );
      paint();
    };

    button.addEventListener("click", () => {
      mode = MODES[(MODES.indexOf(mode) + 1) % MODES.length];
      localStorage.setItem(storageKey, mode);
      apply();
    });

    // Im System-Modus live prefers-color-scheme folgen.
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onSystem = (): void => {
      if (mode === "system") apply();
    };
    mql.addEventListener("change", onSystem);
    ctx.cleanup.push(() => mql.removeEventListener("change", onSystem));

    apply();
  },
});
